import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import './style.css';
import HeartIcon from 'assets/icons/heart.svg';
import { useTranslation } from 'utils/modifiedTranslation';
import { useIsMounted } from 'utils/hooks/useIsMounted';
import { getSurvey } from 'modules/survey/api';
import { SurveyAnswerKeys, SurveyTypes } from 'modules/survey/surveyEnums';
import app from 'new-ui/app';
import TreatmentIntensity from './TreatmentIntensityV2';
import TreatmentSideEffects from './TreatmentSideEffects';
import AssistanceButton from '../AssistanceButton';
import WidgetBtn from '../Button';
import ArrowButton from '../ArrowButton';
import { mockSideEffects, mockIntensities } from './mockData';
import { FEED_GA } from '../constants';

const labels = {
  not_aggressive: 'Not Intense',
  somewhat_aggresive: 'Somewhat Intense',
  very_aggressive: 'Very Intense',
};

const convertArrayToObject = (arr) => arr.reduce((acc, item) => {
  const { value, ...rest } = item;
  acc[value] = { ...rest };
  return acc;
}, {});

const ExperienceWidget = ({
  onClickAddMyExperance, onChangeSelectedDrug, selectedDrug, changesNeed, reviewedDrugs, onReady,
}) => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const [currentDrugIndex, setCurrentDrugIndex] = useState(0);

  const [currentDrug, setCurrentDrug] = useState(null);
  const [sideEffects, setSideEffects] = useState([]);
  const [intensitiesMap, setIntensitiesMap] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [drugs, setDrugs] = useState([]);
  const [patientsCount, setPatientsCount] = useState(0);

  const arrangeData = (drug) => {
    if (drug) {
      const { answerStatistics = [] } = drug;
      const nextSideEffects = answerStatistics.filter((el) => (
        el.answerKey === SurveyAnswerKeys.sideEffects && el.value !== 'no_sideeffects'
      ));
      const intensities = answerStatistics.filter((el) => (
        el.answerKey === SurveyAnswerKeys.aggression
        && ['somewhat_aggresive', 'not_aggressive', 'very_aggressive'].includes(el.value)
      ));

      if (!nextSideEffects.length) {
        nextSideEffects.push(...mockSideEffects);
      }
      if (!intensities.length) {
        intensities.push(...mockIntensities);
      }

      setIntensitiesMap(convertArrayToObject(intensities));
      setSideEffects(nextSideEffects);
    }
  };

  const fetch = async () => {
    app.sendGoogleAnalyticsEvent(FEED_GA.category, FEED_GA.events.experienceViewed, {
      widget: 'treatment experience',
    });
    setIsLoading(true);
    const experiences = await getSurvey(SurveyTypes.treatmentExperience, app.user.info.user_wix_id);

    if (!isMounted()) {
      return;
    }

    const { drugs: nextDrugs } = experiences.data.data;
    const drug = (selectedDrug ? nextDrugs.find((d) => d.name === selectedDrug.name) : selectedDrug) || nextDrugs[nextDrugs.length - 1];
    setCurrentDrug(drug);
    arrangeData(drug);
    setPatientsCount(experiences.data.data.patients_with_same_indication);
    setDrugs(nextDrugs);
    setIsLoading(false);
    onReady(true);
  };
  useEffect(() => {
    fetch();
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (changesNeed.experienceWidget) {
      fetch();
    }
    // eslint-disable-next-line
  }, [changesNeed.experienceWidget]);

  useEffect(() => {
    const drug = drugs[currentDrugIndex];
    setCurrentDrug(drug);
    onChangeSelectedDrug(drug);
    arrangeData(drug);
    // eslint-disable-next-line
  }, [currentDrugIndex]);

  const getIntensities = () => {
    const options = [];
    ['not_aggressive', 'somewhat_aggresive', 'very_aggressive'].forEach((intensity) => {
      options.push({
        percent: 0,
        value: intensity,
        title: labels[intensity],
        ...intensitiesMap[intensity],
      });
    });
    return options;
  };

  const onNext = () => {
    setCurrentDrugIndex((prevIndex) => (prevIndex + 1) % drugs.length);
    app.sendGoogleAnalyticsEvent(FEED_GA.category, FEED_GA.events.experienceInteracted, {
      widget: 'treatment experience',
    });
  };

  const onPrev = () => {
    setCurrentDrugIndex((prevIndex) => (prevIndex - 1 + drugs.length) % drugs.length);
    app.sendGoogleAnalyticsEvent(FEED_GA.category, FEED_GA.events.experienceInteracted, {
      widget: 'treatment experience',
    });
  };

  const onBtnClick = () => {
    onClickAddMyExperance();
    onChangeSelectedDrug(currentDrug);
    app.sendGoogleAnalyticsEvent(FEED_GA.category, FEED_GA.events.experienceInteracted, {
      widget: 'treatment experience',
    });
  };

  const getDrugName = () => {
    let names = '';
    if (currentDrug) {
      names = currentDrug.name.split('+').map((name) => t(`questionnaire.questions.drugs_received.${name}`));
      names = names.join(' + ');
    }
    return names;
  };

  useEffect(() => {
    const drug = selectedDrug || drugs[currentDrugIndex];
    setCurrentDrug(drug);
    arrangeData(drug);
    // eslint-disable-next-line
  }, [selectedDrug]);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {(!isLoading && currentDrug) && (
        <div className="experience-widget">
          <div className={`widget-header ${drugs.length > 0 ? 'arrows' : ''}`}>
            <ArrowButton direction="left" onClick={onPrev} />
            <ArrowButton direction="right" onClick={onNext} />
            <div className="details">
              <div className="label">{getDrugName()}</div>
            </div>
          </div>
          <div className="widget-body">
            {drugs.length > 1 && (
              <>
                <ArrowButton direction="left" onClick={onPrev} />
                <ArrowButton direction="right" onClick={onNext} />
              </>
            )}
            <h2 className="title">{t('statistic_widget.title.main')}</h2>
            <h3 className="sub-title">{t('statistic_widget.title.graph')}</h3>

            <TreatmentIntensity options={getIntensities()} />
            <TreatmentSideEffects sideEffects={sideEffects} />

            {!reviewedDrugs[currentDrug?.name] && (
              <div className="actions">
                <h2 className="title">
                  {`${t('experience_widget.help_patients.p1')} ${patientsCount} ${t('experience_widget.help_patients.p2')}`}
                </h2>
                <WidgetBtn title="Add your experience" onClick={onBtnClick} />
              </div>
            )}
            {reviewedDrugs[currentDrug?.name] && (
              <div className="feedback">
                <img src={HeartIcon} alt="icon" />
                <span>
                  {`${t('statistic_widget.title.feedback.p1')} ${patientsCount} ${t('statistic_widget.title.feedback.p2')}`}
                </span>
              </div>
            )}
          </div>
          <div className="widget-footer">
            <AssistanceButton title={t('statistic_widget.actions.assistance.button')} onClick={() => {}} />
          </div>
        </div>
      )}
    </>
  );
};

ExperienceWidget.propTypes = {
  onReady: PropTypes.func.isRequired,
  onClickAddMyExperance: PropTypes.func.isRequired,
  onChangeSelectedDrug: PropTypes.func.isRequired,
  selectedDrug: PropTypes.object,
  changesNeed: PropTypes.shape({
    experienceWidget: PropTypes.bool,
  }).isRequired,
  reviewedDrugs: PropTypes.object.isRequired,
};

export default ExperienceWidget;
